import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GroupLabel } from '../../shared/ui/group-label';
import { Note } from '../../shared/ui/note';
import { ApiError } from '../../shared/api/errors';
import { cn } from '../../shared/lib/cn';
import { updateSource, type TSource } from '../../entities/source';

type TAddress = NonNullable<TSource['addresses']>[number];

const usage = (address: TAddress): string => {
  const whose = address.host === null ? 'ours' : 'yours';
  if (!address.live) return `${whose} · waiting for DNS, not answering yet`;
  return `${whose} · ${address.requestsWeek.toLocaleString('en-US')} request${address.requestsWeek === 1 ? '' : 's'} this week`;
};

const Option = ({
  address,
  chosen,
  saving,
  onChoose,
}: {
  address: TAddress;
  chosen: boolean;
  saving: boolean;
  onChoose: () => void;
}) => (
  <div
    className={cn(
      'flex items-center gap-3 rounded-control px-input py-2',
      chosen ? 'bg-hover-raised' : 'bg-surface-sunken',
    )}
  >
    <button
      type="button"
      role="radio"
      aria-checked={chosen}
      aria-label={`Use ${address.hostname}`}
      disabled={!address.live || saving}
      onClick={onChoose}
      className={cn(
        'flex size-4 shrink-0 items-center justify-center rounded-full border',
        chosen ? 'border-accent' : 'border-border-strong',
        !address.live && 'opacity-40',
      )}
    >
      {chosen && <span className="size-dot rounded-full bg-accent" />}
    </button>
    <span className="min-w-0 flex-1">
      <span className="block truncate font-mono text-data text-primary">{address.hostname}</span>
      <span className="flex items-center gap-2 text-meta text-muted">
        <span
          aria-hidden
          className={cn('size-dot rounded-full', address.live ? 'bg-success' : 'bg-faint')}
        />
        {usage(address)}
      </span>
    </span>
  </div>
);

// Chosen whether or not the general address is open: client addresses use the same hostname.
export const IngestHostPicker = ({
  source,
  className,
}: {
  source: TSource;
  className?: string;
}) => {
  const client = useQueryClient();
  const addresses = source.addresses ?? [];

  const choose = useMutation({
    mutationFn: (host: string | null) => updateSource(source.id, { ingestHost: host }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['source', source.id] });
      void client.invalidateQueries({ queryKey: ['sources'] });
      void client.invalidateQueries({ queryKey: ['source-clients', source.id] });
    },
  });

  if (addresses.length < 2) return null;

  return (
    <div className={className}>
      <GroupLabel>Domain</GroupLabel>
      <div role="radiogroup" aria-label="Domain" className="flex flex-col gap-2">
        {addresses.map((address) => (
          <Option
            key={address.hostname}
            address={address}
            chosen={(source.ingestHost ?? null) === address.host}
            saving={choose.isPending}
            onChoose={() => choose.mutate(address.host)}
          />
        ))}
      </div>
      <p className="mt-2 text-meta text-faint">
        Used for the general address and every client address. All live domains keep answering, so
        moving senders is safe.
      </p>
      {choose.error && (
        <Note tone="danger" className="mt-2">
          {choose.error instanceof ApiError
            ? choose.error.message
            : 'The domain could not be changed. Nothing was saved.'}
        </Note>
      )}
    </div>
  );
};
